import React from 'react';
import { Link, Typography, colors } from '@mui/material';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';

type PolicyCrossrefProps = {
  xrefKey: string;
  children: React.ReactNode;
};

const PolicyCrossref: React.FC<PolicyCrossrefProps> = ({ xrefKey, children }: PolicyCrossrefProps) => {
  const [manualLink, setManualLink] = React.useState<any>();
  const parts = xrefKey.split('/');
  let scope = 'issued';
  let manualId = '';
  let contentId = '';

  if (parts[0] === 'manuals' && parts[2] === 'content') {
    // Handle format: manuals/{manualId}/content/{contentId}
    manualId = parts[1];
    contentId = parts[3];
  } else {
    // Handle legacy format
    scope = parts[3];
    manualId = parts[4];
    contentId = parts[6];
  }

  const extractPath = React.useCallback((manualJson: Manual, targetId: string) => {
    // First check if it's a preface
    const preface = manualJson.prefaces.find((p) => p.id === targetId);
    if (preface) {
      return {
        isPreface: true,
        prefaceId: preface.id,
        chapterId: '',
        policyId: '',
        sectionId: '',
      };
    }

    // If not a preface, check chapters, policies, sections, and subsections
    let chapterId = '';
    let policyId = '';
    let sectionId = '';
    (manualJson?.chapters || []).forEach((chapter) => {
      if (chapter.policies) {
        chapter.policies.forEach((p) => {
          if (p.id === targetId) {
            policyId = p.id;
            chapterId = chapter.id;
          }
          if (p.sections) {
            p.sections.forEach((s) => {
              if (s.id === targetId) {
                policyId = p.id;
                chapterId = chapter.id;
                sectionId = s.id;
              }
              if (s.subSections) {
                s.subSections.forEach((ss) => {
                  if (ss.id === targetId) {
                    policyId = p.id;
                    chapterId = chapter.id;
                    sectionId = ss.id;
                  }
                });
              }
            });
          }
        });
      }
    });
    return {
      isPreface: false,
      prefaceId: '',
      chapterId,
      policyId,
      sectionId,
    };
  }, []);

  React.useEffect(() => {
    PolicyContentHelper.handleRetrieveCrossref(xrefKey, (data) => {
      const { isPreface, prefaceId, policyId, sectionId } = extractPath(data, contentId);
      if (isPreface) {
        setManualLink(`/admin/policy/${scope}-manuals/${manualId}/preface/${prefaceId}`);
      } else {
        setManualLink(`/admin/policy/${scope}-manuals/${manualId}/policy/${policyId}?s=${sectionId}`);
      }
    });
  }, [contentId, extractPath, manualId, scope, xrefKey]);

  return manualLink && manualLink.length > 0 ? (
    <>
      <Link
        href={manualLink}
        target='_blank'
        rel='noreferrer'
      >
        {typeof children === 'string' ? children.replace(/^(.+?)\1+$/, '$1') : children}
      </Link>
    </>
  ) : (
    <Typography
      component='span'
      variant='body1'
      sx={{ color: colors.grey[500] }}
    >
      {typeof children === 'string' ? children.replace(/^(.+?)\1+$/, '$1') : children}
    </Typography>
  );
};

export default PolicyCrossref;
